import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { clearState } from 'app/app.actions';
import {
    invalidToken,
    login,
    logout,
    requestUpdateBranding,
    tokenNotPresent,
    updateBranding,
    userUpdated,
    validTokenPresent
} from './auth.actions';
import { User } from './user.model';


export interface State {
    loggedIn?: boolean;
    userInfoLoaded: boolean;
    user: User;
    token?: string;
}

export const anonymousUser: User = {
    uid: null,
    username: null,
    avatar: null,
    details: {
        firstName: null,
        lastName: null,
        cellPhone: null,
        company: null,
        profession: null,
        timezone: null,
        signupEpoch: null,
        homeRegion: '',
        areaOfSpecialization: null,
        defaultMapView: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        country: null,
        postalCode: null,
        creaTrademarkStatement: null,
        showBoardLandingPage: false,
    },
    intercom: null,
    branding: null,
    subscriptionValid: false,
    isAdmin: false,
    skillsLearned: [],
    filledCredentials: [],
    propertiesTableQuotaLeft: 0,
    paragonTableQuotaLeft: 0,
    offerRentQuotaLeft: 0,
    agreements: {},
    disclaimers: {},
    sendNotificationsEmail: false,
    notificationsRecurrence: 12,
    needsSsoRelogin: false,
};

export const initialState: State = {
    user: anonymousUser,
    userInfoLoaded: false,
};

export const authReducer = createReducer(
    initialState,
    on(login, validTokenPresent, (state, { token }) => ({
        ...state,
        loggedIn: true,
        error: undefined,
        token: token,
    })),
    on(logout, invalidToken, tokenNotPresent, (state) => ({
        ...state,
        loggedIn: false,
        userInfoLoaded: false,
        user: anonymousUser,
        token: undefined,
        disclaimers: {},
    })),
    on(clearState, state => ({
        ...state,
        loggedIn: false,
    })),
    on(userUpdated, (state, { user, details, disclaimers }) => ({
        ...state,
        userInfoLoaded: true,
        user: {
            ...state.user,
            ...user,
            details: {
                ...state.user.details,
                ...user?.details,
                ...details,
            },
            disclaimers: disclaimers || state.user.disclaimers,
        },
    })),
    on(updateBranding, requestUpdateBranding, (state, { branding }) => ({
        ...state,
        user: {
            ...state.user,
            branding: state.user.branding ? { ...state.user.branding || {}, ...branding } : state.user.branding,
        },
    }))
);

const selector = createFeatureSelector<State>('auth');

export const getAuthLoggedIn = createSelector(selector, state => state.loggedIn);
export const getAuthToken = createSelector(selector, state => state.token);
export const getUserInfo = createSelector(selector, state => state.user);
export const getUserInfoLoaded = createSelector(selector, state => state.userInfoLoaded);
export const getUserIntercom = createSelector(selector, state => state.user.intercom);
export const getFilledCredentials = createSelector(selector, state => state.user.filledCredentials);
export const getPropertiesTableQuotaLeft = createSelector(selector, state => state.user.propertiesTableQuotaLeft);
export const getParagonTableQuotaLeft = createSelector(selector, state => state.user.paragonTableQuotaLeft);
export const getBranding = createSelector(selector, state => state.user.branding);
export const getUserDisclaimers = createSelector(selector, state => state.user.disclaimers);
